// Service to extract guide text and export to Google Docs
import { stripSourceArtifactsFromText } from './automaticGuideResearch';

export const googleDocsExportService = {
  /**
   * Extracts all relevant text content from the itinerary
   */
  extractGuideText(itinerary) {
    const { metadata, points } = itinerary;

    const textContent = {
      guide_name: metadata.name || '',
      guide_description: stripSourceArtifactsFromText(metadata.description),
      theme: metadata.theme || '',
      city: metadata.city || '',
      country: metadata.country || '',
      stops: points.map((point, index) => ({
        stop_number: index + 1,
        name: point.name || '',
        description: stripSourceArtifactsFromText(point.description),
        inDepthDescription: stripSourceArtifactsFromText(point.inDepthDescription),
        historicalContext: stripSourceArtifactsFromText(point.historicalContext),
        culturalSignificance: stripSourceArtifactsFromText(point.culturalSignificance),
        hiddenGems: stripSourceArtifactsFromText(point.hiddenGems)
      }))
    };

    return textContent;
  },

  /**
   * Formats extracted text into readable document content
   */
  formatAsDocumentContent(textContent) {
    let content = '';

    // Header
    content += `${textContent.guide_name}\n`;
    content += `Location: ${textContent.city}, ${textContent.country}\n`;
    content += `Theme: ${textContent.theme}\n\n`;

    // Guide description
    if (textContent.guide_description) {
      content += `GUIDE OVERVIEW\n`;
      content += `${textContent.guide_description}\n\n`;
    }

    // Stops
    content += `STOPS\n`;
    content += `${'='.repeat(50)}\n\n`;

    textContent.stops.forEach(stop => {
      content += `Stop ${stop.stop_number}: ${stop.name}\n`;
      content += `${'-'.repeat(40)}\n`;

      if (stop.description) {
        content += `Description:\n${stop.description}\n\n`;
      }
      if (stop.inDepthDescription) {
        content += `In-Depth Description:\n${stop.inDepthDescription}\n\n`;
      }
      if (stop.historicalContext) {
        content += `Historical Context:\n${stop.historicalContext}\n\n`;
      }
      if (stop.culturalSignificance) {
        content += `Cultural Significance:\n${stop.culturalSignificance}\n\n`;
      }
      if (stop.hiddenGems) {
        content += `Hidden Gems:\n${stop.hiddenGems}\n\n`;
      }

      content += '\n';
    });

    return content;
  },

  /**
   * Creates a Google Doc with the guide text (requires authentication)
   * This uses the Google Drive API via MCP
   */
  async exportToGoogleDoc(itinerary, folderId = null) {
    try {
      // Extract text content
      const textContent = this.extractGuideText(itinerary);
      const documentContent = this.formatAsDocumentContent(textContent);

      // Prepare document title
      const docTitle = `${itinerary.metadata.name} - Guide Text Content`;

      // Log what we're about to export (for debugging)
      console.log('Exporting to Google Docs:', {
        title: docTitle,
        contentLength: documentContent.length,
        stopsCount: textContent.stops.length
      });

      // Prepare payload for Google Docs API
      // Note: This assumes you have Google Drive MCP set up
      // The actual API call will happen through the MCP when available
      return {
        title: docTitle,
        content: documentContent,
        textContent: textContent,
        mimeType: 'application/vnd.google-apps.document'
      };
    } catch (error) {
      console.error('Error preparing Google Docs export:', error);
      throw new Error(`Failed to prepare Google Docs export: ${error.message}`);
    }
  },

  /**
   * Copies content to clipboard as fallback
   */
  copyToClipboard(itinerary) {
    try {
      const textContent = this.extractGuideText(itinerary);
      const documentContent = this.formatAsDocumentContent(textContent);

      navigator.clipboard.writeText(documentContent);
      return true;
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      return false;
    }
  }
};
